import React from "react";
import ReportTable from "../../ReportTable";
import { useInfiniteAsyncScroll } from "../../../../../hooks/useInfiniteAsyncScroll";
import { getUsersReport } from "../../../../../actions";
import { makeStyles } from "@material-ui/core";
import { newColumnConfig, newColumnPercentage, newColumnValueWithNumberComponent } from "../../../TableList";
import { Link } from "react-router-dom";
import { injectIntl } from "react-intl";
import moment from "moment";
import { formatCurrency, formatPercentage, getDateWithLocale, removeEmptyFields, resolveBooleanList } from "../../../../../util";
import AnimatedCardContent from "../../../AnimatedCardContent";
import UsersReportSummary from "../UsersReportSummary";
import UserLink from "../../UserLink";

const useStyles = makeStyles((theme) => ({
  ellipsis: {
    display: "block",
    width: "100%",
    maxWidth: "160px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontSize: theme.typography.caption.fontSize,
  },
  secondary: {
    color: theme.palette.text.secondary,
  },
}));

const fetchUsersReport = async ({ skip, take, params }) => {
  const { selectedMonthYear, selectedUsers, selectedAgents, hasBeneficiary, isEmailConfirmed, ...filters } = params;

  const reportParams = {
    ...filters,
    skip,
    take,

    isEmailConfirmed: resolveBooleanList(isEmailConfirmed),
    hasBeneficiary: resolveBooleanList(hasBeneficiary),

    agentIds: selectedAgents?.map((a) => a.id) ?? [],
    userIds: selectedUsers?.map((c) => c.id) ?? [],
    month: selectedMonthYear.month() + 1,
    year: selectedMonthYear.year(),
  };
  const cleanedFilters = removeEmptyFields(reportParams);

  return getUsersReport(cleanedFilters);
};

const UserListTable = ({ filters, currentUser, intl, orderBy, setOrderBy, setSort, sort }) => {
  const classes = useStyles();

  const {
    items: users,
    additionalData: summary,
    loading,
    error,
    scrollTriggerRef,
  } = useInfiniteAsyncScroll({
    onScroll: fetchUsersReport,
    params: filters,
    take: 120,
    id: "id",
    mapResponse: (res) => ({
      newItems: res?.userReports ?? [],
      total: res?.totals?.totalUsers ?? 0,
      additionalData: res?.totals,
    }),
  });

  const availableColumns = React.useMemo(
    () => [
      {
        name: "userId",
        label: "app.id",
        enable: true,
        definition: () => newColumnConfig({ title: "app.id", field: "userId", type: "string", align: "center", render: (rowData) => <span className={classes.secondary}>{rowData.userId}</span> }),
        exportValue: (rowData) => rowData.userId,
      },
      {
        name: "name",
        label: "app.name",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.name",
            field: "name",
            type: "string",
            render: (rowData) => (
              <Link
                to={"/user-detail/" + rowData.userId}
                className={classes.link}
              >
                {rowData.name}
              </Link>
            ),
          }),
        exportValue: (rowData) => rowData.name,
      },
      {
        name: "email",
        label: "app.email",
        enable: false,
        definition: () =>
          newColumnConfig({
            title: "app.email",
            field: "email",
            type: "string",
            align: "left",
            render: (rowData) => <span className={classes.ellipsis}>{rowData.email}</span>,
          }),
        exportValue: (rowData) => rowData.email,
      },
      {
        name: "role",
        label: "app.role",
        enable: true,
        definition: () =>
          newColumnConfig({ title: "app.role", field: "role", type: "string", align: "center", render: (rowData) => (rowData.role ? intl.formatMessage({ id: `app.enuns.${rowData.role}` }) : "-") }),
        exportValue: (rowData) => (rowData.role ? `${intl.formatMessage({ id: `app.enuns.${rowData.role}` })}` : "-"),
      },
      {
        name: "customerSituation",
        label: "app.situation.customer",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.situation.customer",
            field: "customerSituation",
            type: "string",
            align: "center",
            render: (rowData) => intl.formatMessage({ id: `app.user.situation.${rowData.customerSituation}` }),
          }),
        exportValue: (rowData) => `${intl.formatMessage({ id: `app.user.situation.${rowData.customerSituation}` })}`,
      },
      {
        name: "agentSituation",
        label: "app.situation.agent",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.situation.agent",
            field: "agentSituation",
            type: "string",
            align: "center",
            render: (rowData) => intl.formatMessage({ id: `app.user.situation.${rowData.agentSituation}` }),
          }),
        exportValue: (rowData) => `${intl.formatMessage({ id: `app.user.situation.${rowData.agentSituation}` })}`,
      },
      {
        name: "agent",
        label: "app.agent",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.agent",
            field: "agent",
            type: "string",
            align: "left",
            render: (rowData) => (
              <UserLink
                user={rowData?.agent}
                currentUserId={currentUser.id}
                className={classes.link}
                secondaryClassName={classes.secondary}
                disableLink={currentUser.id === rowData.agent.id}
              />
            ),
          }),
        exportValue: (rowData) => `${rowData.agent?.name ?? ""} (${rowData.agent?.id ?? ""})`,
      },
      {
        name: "manager",
        label: "app.enuns.levelName.AGENT_MANAGER",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.enuns.levelName.AGENT_MANAGER",
            field: "manager",
            type: "string",
            align: "left",
            render: (rowData) => (
              <UserLink
                user={rowData?.manager}
                currentUserId={currentUser.id}
                className={classes.link}
                secondaryClassName={classes.secondary}
                disableLink={currentUser.id === rowData?.agent?.id}
              />
            ),
          }),
        exportValue: (rowData) => `${rowData?.manager?.name ?? ""} (${rowData?.manager?.id ?? ""})`,
      },
      {
        name: "totalContracts",
        label: "app.contract.quantity",
        enable: true,
        definition: () => newColumnConfig({ title: "app.contract.quantity", field: "totalContracts", type: "numeric", align: "center" }),
      },
      {
        name: "totalActiveContracts",
        label: "app.contract.quantity.active",
        enable: true,
        definition: () => newColumnConfig({ title: "app.contract.quantity.active", field: "totalActiveContracts", type: "numeric", align: "center" }),
      },
      {
        name: "custodialValue",
        label: "app.custodian.total",
        enable: true,
        definition: () => newColumnValueWithNumberComponent("app.custodian.total", "custodialValue", "", currentUser.hideValues),
        exportValue: (rowData) => formatCurrency({ value: rowData.custodialValue, userLocale: currentUser.locale }),
      },
      {
        name: "custodialPercent",
        label: "app.percentage.custodialValue",
        enable: true,
        definition: () => newColumnPercentage("app.percentage.custodialValue", "custodialPercent", true),
        exportValue: (rowData) => formatPercentage({ value: rowData.custodialPercent, userLocale: currentUser.locale }),
      },
      {
        name: "averageDuration",
        label: "app.report.averageDuration",
        enable: true,
        definition: () => newColumnConfig({ title: "app.report.averageDuration", field: "averageDuration", type: "string", align: "center" }),
      },
      {
        name: "preferredContractType",
        label: "app.report.preferredContractType",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.report.preferredContractType",
            field: "preferredContractType",
            type: "string",
            align: "center",
            render: (rowData) => (rowData.preferredContractType ? intl.formatMessage({ id: `app.enuns.${rowData.preferredContractType}` }) : "-"),
          }),
        exportValue: (rowData) => `${intl.formatMessage({ id: `app.enuns.${rowData.preferredContractType}` })}`,
      },
      {
        name: "birthday",
        label: "app.dateOfBirth",
        enable: false,
        definition: () =>
          newColumnConfig({
            title: "app.dateOfBirth",
            field: "birthday",
            type: "date",
            align: "center",
            render: (rowData) => (rowData.birthday ? moment(rowData.birthday).format("DD/MM/YYYY") : "N/D"),
          }),
        exportValue: (rowData) => (rowData.birthday ? getDateWithLocale(rowData.birthday, currentUser.locale) : "N/D"),
      },
      {
        name: "age",
        label: "app.report.age",
        enable: false,
        definition: () => newColumnConfig({ title: "app.report.age", field: "age", type: "string", align: "center", render: (rowData) => (rowData.age ? rowData.age : "N/D") }),
      },
      {
        name: "taxResidence",
        label: "app.taxResidence",
        enable: false,
        definition: () => newColumnConfig({ title: "app.taxResidence", field: "taxResidence", type: "string", align: "center" }),
      },
      {
        name: "country",
        label: "app.country",
        enable: false,
        definition: () => newColumnConfig({ title: "app.country", field: "country", type: "string", align: "center" }),
      },
      { name: "state", label: "app.state", enable: false, definition: () => newColumnConfig({ title: "app.state", field: "state", type: "string", align: "center" }) },
      { name: "city", label: "app.city", enable: false, definition: () => newColumnConfig({ title: "app.city", field: "city", type: "string", align: "center" }) },
      {
        name: "telefone",
        label: "app.phone",
        enable: false,
        definition: () => newColumnConfig({ title: "app.phone", field: "telefone", type: "string", align: "center", render: (rowData) => (rowData?.phones?.[0] ? ` ${rowData.phones[0]}` : "-") }),
        exportValue: (rowData) => (rowData?.phones?.[0] ? `'${rowData.phones[0]}` : "-"),
      },
      {
        name: "telefone2",
        label: "app.phone2",
        enable: false,
        definition: () =>
          newColumnConfig({
            title: "app.phone2",
            field: "telefone2",
            type: "string",
            align: "center",
            render: (rowData) => {
              return rowData?.phones?.[1] ? ` ${rowData.phones[1]}` : "-";
            },
          }),
        exportValue: (rowData) => (rowData?.phones?.[1] ? `'${rowData.phones[1]}` : "-"),
      },
      {
        name: "hasBeneficiary",
        label: "app.report.hasBeneficiary",
        enable: false,
        definition: () =>
          newColumnConfig({
            title: "app.report.hasBeneficiary",
            field: "hasBeneficiary",
            type: "string",
            align: "center",
            render: (rowData) => intl.formatMessage({ id: `app.${rowData.hasBeneficiary ? "yes" : "no"}` }),
          }),
        exportValue: (rowData) => `${intl.formatMessage({ id: `app.${rowData.hasBeneficiary ? "yes" : "no"}` })}`,
      },
      {
        name: "isEmailConfirmed",
        label: "app.email.confirmed",
        enable: false,
        definition: () =>
          newColumnConfig({
            title: "app.email.confirmed",
            field: "isEmailConfirmed",
            type: "string",
            align: "center",
            render: (rowData) => intl.formatMessage({ id: `app.${rowData.isEmailConfirmed ? "yes" : "no"}` }),
          }),
        exportValue: (rowData) => `${intl.formatMessage({ id: `app.${rowData.isEmailConfirmed ? "yes" : "no"}` })}`,
      },
      {
        name: "apt",
        label: "app.apt",
        enable: false,
        definition: () =>
          newColumnConfig({ title: "app.apt", field: "isApt", type: "string", align: "center", render: (rowData) => intl.formatMessage({ id: `app.${rowData.isApt ? "yes" : "no"}` }) }),
        exportValue: (rowData) => `${intl.formatMessage({ id: `app.${rowData.isApt ? "yes" : "no"}` })}`,
      },
      {
        name: "createdAt",
        label: "app.dateRegister",
        enable: false,
        definition: () =>
          newColumnConfig({
            title: "app.dateRegister",
            field: "createdAt",
            type: "date",
            align: "center",
            render: (rowData) => (rowData?.createdAt ? moment(rowData.createdAt).format("DD/MM/YYYY") : "N/D"),
          }),
        exportValue: (rowData) => getDateWithLocale(rowData.createdAt, currentUser.locale),
      },
      {
        name: "activationDate",
        label: "app.date.active",
        enable: false,
        definition: () =>
          newColumnConfig({
            title: "app.date.active",
            field: "activationDate",
            type: "date",
            align: "center",
            render: (rowData) => (rowData?.activationDate ? moment(rowData.activationDate).format("DD/MM/YYYY") : "N/D"),
          }),
        exportValue: (rowData) => {
          return rowData?.activationDate ? getDateWithLocale(rowData.activationDate, currentUser.locale) : "N/D";
        },
      },
    ],
    [classes.ellipsis, classes.link, classes.secondary, currentUser.hideValues, currentUser.id, currentUser.locale, intl],
  );

  return (
    <>
      <UsersReportSummary
        summary={summary}
        disabled={loading || error}
      />
      <AnimatedCardContent>
        <ReportTable
          availableColumns={availableColumns}
          currentUser={currentUser}
          title="app.list.users"
          data={users}
          loading={loading}
          error={error}
          enableCopy
          fetchAllData={fetchUsersReport}
          rowsPath="userReports"
          params={filters}
          reportKey="reportUsers"
          orderBy={orderBy}
          sort={sort}
          setOrderBy={setOrderBy}
          setSort={setSort}
        />
      </AnimatedCardContent>
      <div ref={scrollTriggerRef} />
    </>
  );
};

export default injectIntl(UserListTable);
